import { ChevronsDownUp, ChevronsUpDown, History, Route, Search, X } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";

import {
  EMPTY_B2C_FILTERS,
  isFiltering,
  type B2cBoardFilters,
} from "@/application/use-cases/b2c/filter-b2c-board";
import {
  ITERATION_STATUS_LABEL,
  KIND_LABEL,
  KIND_ORDER,
  PRIORITY_LABEL,
  PRIORITY_ORDER,
} from "@/presentation/data/b2c-board";
import { DELIVERY_STATUS_LABEL, DELIVERY_STATUS_ORDER } from "@/presentation/data/board";
import type {
  B2cStageRow,
  B2cTaskKind,
  B2cTaskPriority,
  B2cTaskStatus,
  IterationStatus,
} from "@/infrastructure/supabase/b2c-board.types";

const ITERATION_ORDER: IterationStatus[] = ["planned", "marathon", "feedback", "merged"];

/** The MVP board's toolbar, adapted: stage and marathon state instead of domain and critical path. */
export function B2cBoardToolbar({
  filters,
  onFiltersChange,
  stages,
  allOpen,
  onToggleAll,
  onOpenActivity,
}: {
  filters: B2cBoardFilters;
  onFiltersChange: (next: B2cBoardFilters) => void;
  stages: B2cStageRow[];
  allOpen: boolean;
  onToggleAll: () => void;
  onOpenActivity: () => void;
}) {
  const set = (patch: Partial<B2cBoardFilters>) => onFiltersChange({ ...filters, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-2 print:hidden">
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
        <Input
          value={filters.search}
          onChange={(e) => set({ search: e.target.value })}
          placeholder="Search tasks, codes, workstreams…"
          aria-label="Search the B2C board"
          className="h-9 w-[240px] pl-8 text-[13px]"
        />
      </div>

      <Select
        value={filters.status}
        onValueChange={(v) => set({ status: v as B2cTaskStatus | "all" })}
      >
        <SelectTrigger className="h-9 w-[140px] text-[13px]" aria-label="Task status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            All statuses
          </SelectItem>
          {DELIVERY_STATUS_ORDER.map((s) => (
            <SelectItem key={s} value={s} className="text-[13px]">
              {DELIVERY_STATUS_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.stage} onValueChange={(v) => set({ stage: v })}>
        <SelectTrigger className="h-9 w-[200px] text-[13px]" aria-label="Stage">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            All stages
          </SelectItem>
          {stages.map((s) => (
            <SelectItem key={s.key} value={s.key} className="text-[13px]">
              {s.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.iteration}
        onValueChange={(v) => set({ iteration: v as IterationStatus | "all" })}
      >
        <SelectTrigger className="h-9 w-[180px] text-[13px]" aria-label="Iteration state">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            Any iteration state
          </SelectItem>
          {ITERATION_ORDER.map((s) => (
            <SelectItem key={s} value={s} className="text-[13px]">
              {ITERATION_STATUS_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.priority}
        onValueChange={(v) => set({ priority: v as B2cTaskPriority | "all" })}
      >
        <SelectTrigger className="h-9 w-[150px] text-[13px]" aria-label="Priority">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            Any priority
          </SelectItem>
          {PRIORITY_ORDER.map((p) => (
            <SelectItem key={p} value={p} className="text-[13px]">
              {PRIORITY_LABEL[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.kind} onValueChange={(v) => set({ kind: v as B2cTaskKind | "all" })}>
        <SelectTrigger className="h-9 w-[140px] text-[13px]" aria-label="Kind">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            Any kind
          </SelectItem>
          {KIND_ORDER.map((k) => (
            <SelectItem key={k} value={k} className="text-[13px]">
              {KIND_LABEL[k]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant={filters.e2eOnly ? "default" : "outline"}
        aria-pressed={filters.e2eOnly}
        size="sm"
        className="h-9 gap-1.5"
        onClick={() => set({ e2eOnly: !filters.e2eOnly })}
      >
        <Route className="size-3.5" />
        E2E proofs
      </Button>

      {isFiltering(filters) ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 gap-1.5"
          onClick={() => onFiltersChange(EMPTY_B2C_FILTERS)}
        >
          <X className="size-3.5" />
          Clear
        </Button>
      ) : null}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-auto h-9 gap-1.5"
        onClick={onToggleAll}
      >
        {allOpen ? (
          <ChevronsDownUp className="size-3.5" />
        ) : (
          <ChevronsUpDown className="size-3.5" />
        )}
        {allOpen ? "Collapse all" : "Expand all"}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 gap-1.5"
        onClick={onOpenActivity}
      >
        <History className="size-3.5" />
        Activity
      </Button>
    </div>
  );
}
